import { useMutation, useQuery, useQueryClient, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { rfqsApi } from './api';
import type { AwardRfqPayload, CreateRfqPayload, Rfq, RfqListParams, RfqListResponse, RfqStats, SendRfqPayload, SubmitQuotePayload, UpdateRfqPayload } from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useRfqs(params: RfqListParams): UseQueryResult<RfqListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['rfqs', tenantId, 'list', params],
    queryFn: () => rfqsApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useRfqStats(params: { location_id?: string }): UseQueryResult<RfqStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['rfqs', tenantId, 'stats', params],
    queryFn: () => rfqsApi.stats(params),
    staleTime: 15_000,
  });
}

export function useRfq(id: string | undefined): UseQueryResult<Rfq | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['rfqs', tenantId, 'detail', id],
    queryFn: () => (id ? rfqsApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

function useInvalidateRfqs() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['rfqs', tenantId] });
    if (id) void qc.invalidateQueries({ queryKey: ['rfqs', tenantId, 'detail', id] });
  };
}

export function useCreateRfq() {
  const invalidate = useInvalidateRfqs();
  return useMutation({
    mutationFn: (payload: CreateRfqPayload) => rfqsApi.create(payload),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateRfq() {
  const invalidate = useInvalidateRfqs();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRfqPayload }) => rfqsApi.update(id, payload),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useSendRfq() {
  const invalidate = useInvalidateRfqs();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SendRfqPayload }) => rfqsApi.send(id, payload),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useSubmitRfqQuote() {
  const invalidate = useInvalidateRfqs();
  return useMutation({
    mutationFn: ({ id, rfqSupplierId, payload }: { id: string; rfqSupplierId: string; payload: SubmitQuotePayload }) =>
      rfqsApi.submitQuote(id, rfqSupplierId, payload),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

export function useCloseRfq() {
  const invalidate = useInvalidateRfqs();
  return useMutation({ mutationFn: (id: string) => rfqsApi.close(id), onSuccess: (_d, id) => invalidate(id) });
}

/** Awards lines per supplier: one PO per winner, RFQ → CLOSED. Also refreshes PO lists. */
export function useAwardRfq() {
  const invalidate = useInvalidateRfqs();
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AwardRfqPayload }) => rfqsApi.award(id, payload),
    onSuccess: (_d, vars) => {
      invalidate(vars.id);
      void qc.invalidateQueries({ queryKey: ['purchase-orders', tenantId] });
    },
  });
}

export function useCancelRfq() {
  const invalidate = useInvalidateRfqs();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rfqsApi.cancel(id, reason),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
}

/** Fetches the RFQ PDF blob; read-only, no invalidation. */
export function useDownloadRfqPdf() {
  return useMutation({ mutationFn: (id: string) => rfqsApi.downloadPdf(id) });
}
